import { Injectable, inject } from '@angular/core';
import { SqlitePorterHelper } from '@core/helpers/sqlite-porter.helper';
import { AddRedlineRequest } from '@core/interfaces/requests/add-redline-request';
import { DatabaseService } from '@core/services/database.service';
import { ActionRequest, QueueService } from '@core/services/queue.service';
import { Redline } from '@orm-entities/redline';
import { WhiteboardUpdate } from '@osc-utils/models/whiteboard-update';
import { Observable, from, map, of, switchMap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class RedlinesService {
  readonly #databaseService = inject(DatabaseService);
  readonly #queueService = inject(QueueService);
  #redlinesCache: WhiteboardUpdate[] = [];

  public load(fileId: string): Observable<WhiteboardUpdate[]> {
    return this.#databaseService
      .select(Redline, { fileId }, { pointOrder: 'ASC' })
      .pipe(map(redlines => redlines.map(({ x, y, type, UUID }) => ({ x, y, type, UUID }))));
  }

  public addRedlines(fileId: string, request: AddRedlineRequest, taskId: string): Observable<void> {
    this.#redlinesCache = [...this.#redlinesCache, ...request.points];
    if (!this.#isEndOfShape(request.points)) {
      return of(undefined);
    }
    const allPoints = this.#getAllPointsForShape(request.points[0].UUID);
    return this.#persistRedlines(fileId, allPoints, taskId);
  }

  public deleteRedline(redlineId: string, taskId: string): Observable<void> {
    return this.#databaseService.delete(Redline, { UUID: redlineId }).pipe(
      switchMap(() => {
        const actionRequest: ActionRequest = {
          actionType: 'DeleteRedline',
          request: JSON.stringify({ drawnShapeId: redlineId }),
          requestedOnUtc: new Date().toUTCString(),
          taskId,
        };
        return from(this.#queueService.enqueue(actionRequest));
      }),
      map(() => undefined),
    );
  }

  public deleteAllRedlines(fileId: string, taskId: string): Observable<void> {
    return this.#databaseService.delete(Redline, { fileId }).pipe(
      switchMap(() => {
        const actionRequest: ActionRequest = {
          actionType: 'DeleteAllRedline',
          request: JSON.stringify({ fileId }),
          requestedOnUtc: new Date().toUTCString(),
          taskId,
        };
        return from(this.#queueService.enqueue(actionRequest));
      }),
      map(() => undefined),
    );
  }

  #persistRedlines(fileId: string, points: WhiteboardUpdate[], taskId: string): Observable<void> {
    return this.#databaseService.selectMax(Redline, { pointOrder: 'DESC' }).pipe(
      switchMap(maxRow => {
        const countValue = (maxRow ? maxRow.pointOrder : 0) + 1;
        const redlinesObject = points.map((redline, index) => ({
          x: redline.x,
          y: redline.y,
          type: redline.type,
          UUID: redline.UUID,
          pointOrder: countValue + index,
          fileId,
        }));
        const res = { data: { inserts: { Redline: redlinesObject } } };
        return this.#databaseService.executeSql(SqlitePorterHelper.buildSqlStatement(res), []);
      }),
      switchMap(() => {
        const actionRequest: ActionRequest = {
          actionType: 'AddRedline',
          request: JSON.stringify({
            fileId,
            points: points.map(item => ({
              location: { left: item.x, top: item.y },
              type: item.type,
              drawnShapeId: item.UUID,
            })),
          }),
          fileId,
          requestedOnUtc: new Date().toUTCString(),
          taskId,
        };
        return from(this.#queueService.enqueue(actionRequest));
      }),
      map(() => undefined),
    );
  }

  #isEndOfShape(points: WhiteboardUpdate[]): boolean {
    return !!points.find(item => item.type === 2);
  }

  #getAllPointsForShape(shapeId: string): WhiteboardUpdate[] {
    return this.#redlinesCache.filter(point => point.UUID === shapeId);
  }
}
